import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { specialties, clinicInfo } from '@/mocks/homeData';

export default function Especialidades() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-400 via-transparent to-transparent" />
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
            >
              O que fazemos
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 mt-4 leading-[1.05]"
            >
              Nossas<br />
              <span className="italic font-light">Especialidades.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-[680px] mx-auto mt-6 text-foreground-600 text-base leading-relaxed"
            >
              Um portfólio completo de serviços em saúde feminina, com atendimento personalizado e tecnologia de ponta para cuidar de você em cada fase da vida.
            </motion.p>
          </div>
        </section>

        {/* Specialties Grid */}
        <section className="relative bg-background-50 py-24 md:py-32 lg:py-40">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
              {specialties.map((spec, index) => (
                <motion.div
                  key={spec.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: 0.08 * index }}
                  className="group relative bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:shadow-[0_25px_60px_-20px_rgba(0,0,0,0.05)] transition-all duration-500"
                >
                  <div className="flex flex-col lg:flex-row">
                    {/* Image */}
                    <div className="lg:w-[280px] flex-shrink-0 overflow-hidden">
                      <div className="aspect-[4/3] lg:aspect-[3/4] relative">
                        <img
                          src={spec.image}
                          alt={spec.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />
                      </div>
                    </div>
                    {/* Content */}
                    <div className="flex-1 p-6 md:p-8 lg:p-10 flex flex-col justify-center">
                      <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                        <i className={`${spec.icon} text-xl`}></i>
                      </div>
                      <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3 leading-tight">
                        {spec.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {spec.description}
                      </p>
                      <div className="mt-5">
                        <a
                          href={`https://wa.me/${clinicInfo.whatsapp}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-xs tracking-[0.08em] uppercase text-primary-500 hover:text-primary-600 font-label transition-colors duration-200 whitespace-nowrap"
                        >
                          Agendar consulta
                          <i className="ri-arrow-right-line"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative bg-background-100 py-20 md:py-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.15]"
            >
              Não encontrou o que<br />
              <span className="italic font-light">procurava?</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 text-foreground-600 text-sm max-w-md mx-auto"
            >
              Entre em contato conosco. Teremos prazer em esclarecer suas dúvidas e encontrar a melhor solução para você.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8"
            >
              <a
                href={`https://wa.me/${clinicInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                Falar no WhatsApp
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}